import { Component, ElementRef, Inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SendPhoneCode, StiiraError, VerifyPhoneCode } from '@core/models';
import { ErrorService, LayoutService, ProfileService, SnackbarService } from '@core/services';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { indicate, closeDialogOnEscape } from '@shared/helpers';
import { Observable, Subject } from 'rxjs';

@Component({
    selector: 'app-change-phone-number',
    templateUrl: './change-phone-number.component.html',
    styleUrls: ['./change-phone-number.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChangePhoneNumberComponent implements OnInit {
  static readonly componentName = 'ChangePhoneNumberComponent';

  @ViewChild('otpInput') otpInput: ElementRef<HTMLInputElement>;

  public position = position;
  public onDragEnded = onDragEnded;

  form: UntypedFormGroup;
  verifyForm: UntypedFormGroup;
  isSubmitting$ = new Subject<boolean>();
  public isMobile$: Observable<boolean>;
  // resend and verify share isSubmitting$, so this says which one is running and the template puts the spinner in that button
  submittingAction: 'resend' | 'verify';
  showVerificationForm: boolean;

  get phoneNumber(): FormControl {
    return this.form.get('phoneNumber') as FormControl;
  }

  get otp(): FormControl {
    return this.verifyForm.get('otp') as FormControl;
  }

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ChangePhoneNumberComponent>,
    private profileService: ProfileService,
    private snackbarService: SnackbarService,
    private errorService: ErrorService,
    @Inject(MAT_DIALOG_DATA) public sysText,
    private layoutService: LayoutService
  ) { }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();
    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.dialogRef.close());

    this.showVerificationForm = false;
    this.form = this.fb.group({
      phoneNumber: [null, Validators.required]
    });
    this.verifyForm = this.fb.group({
      otp: [null, Validators.required]
    });
  }

  changePhoneNumber(): void {
    this.sendCode(this.phoneNumber.value)
  }

  confirmPhoneNumber(): void {
    this.submittingAction = 'verify';
    const setPhoneModel: VerifyPhoneCode = {
      phoneNumber: this.phoneNumber.value,
      otp: this.verifyForm.value.otp
    }
    this.profileService
      .verifyChangePhoneCode(setPhoneModel)
      .pipe(indicate(this.isSubmitting$))
      .subscribe(
        () => {
          this.snackbarService.open("Phone number successfully set!", 'Dismiss');
          this.dialogRef.close();
        },
        (err: StiiraError) => this.errorService.setFormModelStateErrors(this.verifyForm, err.modelStateErrors)
      );
  }

  onResend(): void {
    this.submittingAction = 'resend';
    this.sendCode(this.phoneNumber.value);
  }

  sendCode(number: string): void {
    const sendCode: SendPhoneCode = {
      phoneNumber: number
    }
    this.profileService.sendChangePhoneCode(sendCode)
    .pipe(indicate(this.isSubmitting$))
      .subscribe(
        () => {
          // the first send swaps the phone step for the code step, which removes the focused button, so focus moves to the code
          // field once it's rendered
          if (!this.showVerificationForm) {
            setTimeout(() => this.otpInput?.nativeElement.focus());
          }
          this.showVerificationForm = true;
          this.snackbarService.open('Verification code sent!', 'Dismiss');
        },
        (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors)
      );
  }
}